import React from 'react';

import { FileDisplay } from '@components/FileDisplay';
import { instructionStrings } from '@data/instruction';
import { BlanketInsurance } from '@models/next/BlanketInsurance';
import { RemoteFile } from '@models/next/FileType';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDate } from '@utils/types';
import { stringify } from 'query-string';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    insuranceFilesExist: '保険証書あり',
    insuranceFilesAbsent: '保険証書なし',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    backgroundColor: theme.palette.grey[50],
    borderRadius: 2,
  },
  cardLinkDetails: {
    cursor: 'pointer',
  },
  bar: {
    backgroundColor: theme.palette.primary.main,
    minWidth: 12,
    borderTopLeftRadius: 2,
    borderBottomLeftRadius: 2,
  },
  contentMainCol: {
    ...common.flexColumn,
    flex: 1,
    height: '100%',
    paddingRight: '87px',
    overflow: 'hidden',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      paddingRight: '10px',
    },
  },
  contentMainHead: {
    ...common.flexInline,
    height: 40,
    alignItems: 'center',
  },
  commodityNameCol: {
    ...common.middleText,
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
  commodityNoCol: {
    ...common.middleText,
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: theme.palette.text.primary,
    fontWeight: 'bold',
  },
  contentMainBody: {
    flex: 1,
    ...common.flexColumn,
    ...common.middleText,
    color: theme.palette.grey[800],
    width: '100%',
  },
  commodityDesc: {
    ...common.middleText,
    ...common.wordBreakBreakAll,
    overflow: 'hidden',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    color: theme.palette.grey[700],
  },
  contentPreviewCol: {
    maxWidth: 99,
    height: '100%',
    backgroundColor: '',
    ...common.flexColumnCenter,
    ...common.justifyCenter,
  },
  contentEditCol: {
    width: 83,
    height: '100%',
    paddingTop: '4px',
    ...common.middleText,
    ...common.flexInline,
    ...common.justifyCenter,
    [theme.breakpoints.down('sm')]: {
      width: 50,
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  commodityFieldEmpty: {
    color: theme.palette.grey[500],
  },
  content: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.middleText,
    flex: 1,
    paddingLeft: 16,
    paddingRight: 16,
  },
  textGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: '1rem',
    rowGap: '12px',
  },
  filesContainer: {
    height: 100,
    width: 100,
  },
  filesDisplay: {
    width: '100%',
    height: '100%',
  },
  linkContainer: {
    height: 100,
    paddingLeft: 30,
    paddingRight: 14,
  },
}));

const BlanketInsuranceFilesDisplay: React.FC<{ files: RemoteFile[] }> = ({ files }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.filesContainer}>
      <FileDisplay
        className={classes.filesDisplay}
        label={files.length > 0 ? strings.insuranceFilesExist : strings.insuranceFilesAbsent}
        file={files?.[0]}
      />
    </div>
  );
};

export const BlanketInsuranceCard: React.FC<{ item: BlanketInsurance }> = ({ item }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.container}>
      <div className={classes.bar} />

      <div className={classes.content}>
        <div className={classes.textGrid}>
          <div>{instructionStrings.insuranceCompany}</div>
          <div>{item.company || '-'}</div>

          <div>{instructionStrings.insurancePolicyNo}</div>
          <div>{item.policy_no || '-'}</div>

          <div>{instructionStrings.insurancePeriod}</div>
          <div>
            {formatDate(item.start)} - {formatDate(item.end)}
          </div>
        </div>

        <BlanketInsuranceFilesDisplay files={item.blanket_insurance_files} />

        <div className={classes.linkContainer}>
          <Link
            className={classes.link}
            to={{
              pathname: url('/utilities/BlanketInsurance/BlanketInsuranceDetails'),
              search: stringify({
                id: item.id,
              }),
            }}
          >
            {formStrings.edit}
          </Link>
        </div>
      </div>
    </div>
  );
};
